/**
 * Monad-specific footer component displaying live MON price + social links + token search
 * Only queries tokens_monad table and shows MON price instead of ETH
 */

import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useMonadTokens } from '../../hooks/useMonadTokens';

// Monad Brand Colors
const MONAD_PURPLE = '#836EF9';
const MONAD_BLUE = '#200052';
const MONAD_BERRY = '#A0055D';
const MONAD_BLACK = '#0E100F';
const MONAD_OFF_WHITE = '#FBFAF9';

const FooterContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50px;
  background: #000000;
  border-top: 1px solid rgba(131, 110, 249, 0.2);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 0 30px;
  gap: 20px;
  z-index: 1000;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    padding: 0 15px;
    gap: 12px;
    height: 45px;
  }
`;

const PriceItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const CoinLogo = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;

  @media (max-width: 768px) {
    width: 20px;
    height: 20px;
  }
`;

const PriceText = styled.span`
  color: ${MONAD_OFF_WHITE};
  font-size: 14px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(131, 110, 249, 0.1);
  border-radius: 6px;
  transition: all 0.2s ease;
  margin-left: 10px;

  &:hover {
    background: rgba(131, 110, 249, 0.3);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
    margin-left: 5px;
  }
`;

const SocialIcon = styled.img`
  width: 18px;
  height: 18px;
  object-fit: contain;

  @media (max-width: 768px) {
    width: 16px;
    height: 16px;
  }
`;

const SearchContainer = styled.div`
  flex: 0 0 auto;
  width: 280px;
  position: relative;

  @media (max-width: 768px) {
    width: 180px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  height: 36px;
  padding: 0 12px 0 32px;
  background: rgba(32, 0, 82, 0.8);
  border: 1px solid rgba(131, 110, 249, 0.3);
  border-radius: 8px;
  color: ${MONAD_OFF_WHITE};
  font-size: 13px;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: rgba(131, 110, 249, 0.6);
    background: rgba(32, 0, 82, 0.95);
  }

  &::placeholder {
    color: rgba(251, 250, 249, 0.5);
  }

  @media (max-width: 768px) {
    height: 32px;
    font-size: 12px;
    padding: 0 10px 0 28px;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(251, 250, 249, 0.5);
  font-size: 14px;
  pointer-events: none;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    width: 10px;
    height: 10px;
    border: 2px solid currentColor;
    border-radius: 50%;
    position: absolute;
    top: 0;
    left: 0;
  }

  &::after {
    content: '';
    width: 2px;
    height: 5px;
    background: currentColor;
    position: absolute;
    bottom: 0;
    right: 0;
    transform: rotate(-45deg);
    border-radius: 1px;
  }

  @media (max-width: 768px) {
    left: 8px;
    width: 14px;
    height: 14px;
  }
`;

const SearchResultsPanel = styled.div<{ $show: boolean }>`
  position: fixed;
  bottom: 60px;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 600px;
  background: linear-gradient(135deg, rgba(32, 0, 82, 0.98) 0%, rgba(14, 16, 15, 0.98) 100%);
  border: 1px solid rgba(131, 110, 249, 0.3);
  border-radius: 12px;
  padding: ${props => props.$show ? '20px' : '0'};
  max-height: ${props => props.$show ? '400px' : '0'};
  overflow-y: auto;
  opacity: ${props => props.$show ? '1' : '0'};
  visibility: ${props => props.$show ? 'visible' : 'hidden'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 999;
  backdrop-filter: blur(12px);
  box-shadow: ${props => props.$show ? '0 -8px 32px rgba(0, 0, 0, 0.4)' : 'none'};

  @media (max-width: 768px) {
    width: 95%;
    max-height: ${props => props.$show ? '300px' : '0'};
    padding: ${props => props.$show ? '16px' : '0'};
    bottom: 50px;
  }
`;

const TokenResultItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: rgba(14, 16, 15, 0.5);
  border-radius: 10px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(131, 110, 249, 0.1);
    transform: translateX(4px);
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    padding: 10px;
    gap: 10px;
  }
`;

const TokenResultLogo = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(131, 110, 249, 0.2);
  border: 1px solid rgba(131, 110, 249, 0.3);

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
  }
`;

const TokenResultInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TokenResultName = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${MONAD_OFF_WHITE};

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const TokenResultSymbol = styled.div`
  font-size: 12px;
  color: rgba(251, 250, 249, 0.6);

  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const TokenResultAddress = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: rgba(14, 16, 15, 0.6);
  border-radius: 6px;
  font-family: monospace;
  font-size: 11px;
  color: ${MONAD_PURPLE};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(131, 110, 249, 0.2);
  }

  @media (max-width: 768px) {
    font-size: 10px;
    padding: 5px 8px;
  }
`;

const CopyIcon = styled.span`
  font-size: 12px;
  color: rgba(251, 250, 249, 0.5);
`;

const NoResultsText = styled.div`
  text-align: center;
  color: rgba(251, 250, 249, 0.5);
  font-size: 14px;
  padding: 20px;
`;

const MonadPriceFooter: React.FC = () => {
  const [monPrice, setMonPrice] = useState<number | null>(null);
  const [footerSearch, setFooterSearch] = useState('');
  
  // Get all Monad tokens for search
  const { tokens: allMonadTokens } = useMonadTokens('');

  // WMON (Wrapped Monad) address on Monad network
  // For native MON, we use zero address. Relay API should handle native currency pricing
  const NATIVE_MON_ADDRESS = '0x0000000000000000000000000000000000000000';
  const MONAD_CHAIN_ID = 143;

  // Filter tokens for search (only Monad tokens)
  const filteredTokens = useMemo(() => {
    if (!footerSearch || footerSearch.length < 2) return [];
    const search = footerSearch.toLowerCase();
    return allMonadTokens.filter(token => 
      token.name.toLowerCase().includes(search) ||
      token.symbol.toLowerCase().includes(search) ||
      token.contract_address?.toLowerCase().includes(search)
    ).slice(0, 5);
  }, [allMonadTokens, footerSearch]);

  // Fetch MON price from Relay Protocol API
  useEffect(() => {
    const fetchMonPrice = async () => {
      try {
        // Fetch native MON price using Relay API with Monad chain ID
        // Use zero address for native currency
        const RELAY_API_BASE = 'https://api.relay.link';
        const url = `${RELAY_API_BASE}/currencies/token/price?address=${NATIVE_MON_ADDRESS}&chainId=${MONAD_CHAIN_ID}&referrer=mavrk.ink`;
        
        console.log('[MonadPriceFooter] Fetching MON price from:', url);
        const response = await fetch(url);
        
        if (!response.ok) {
          console.warn(`[MonadPriceFooter] Relay API returned ${response.status} for MON price`);
          return;
        }

        const data = await response.json();
        if (typeof data.price === 'number' && data.price > 0) {
          setMonPrice(data.price);
          console.log('[MonadPriceFooter] MON price:', data.price);
        } else {
          console.warn('[MonadPriceFooter] Invalid price response:', data);
        }
      } catch (error) {
        console.error('[MonadPriceFooter] Failed to fetch MON price:', error);
      }
    };

    // Fetch immediately
    fetchMonPrice();

    // Refresh every 30 seconds
    const interval = setInterval(fetchMonPrice, 30000);

    return () => clearInterval(interval);
  }, []);

  const formatPrice = (price: number | null): string => {
    if (price === null) return 'Loading...';
    return `$${price.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    })}`;
  };

  const handleTokenClick = (token: any) => {
    window.location.href = `/monad/token/${token.contract_address}`;
  };

  return (
    <>
      {/* Search Results Panel - Expands upward */}
      <SearchResultsPanel $show={footerSearch.length >= 2 && filteredTokens.length > 0}>
        {filteredTokens.map((token) => (
          <TokenResultItem key={token.contract_address} onClick={() => handleTokenClick(token)}>
            <TokenResultLogo 
              src={token.logo_url || '/default-token.png'} 
              alt={token.symbol}
              onError={(e) => { (e.target as HTMLImageElement).src = '/default-token.png'; }}
            />
            <TokenResultInfo>
              <TokenResultName>{token.name}</TokenResultName>
              <TokenResultSymbol>${token.symbol}</TokenResultSymbol>
            </TokenResultInfo>
            <TokenResultAddress 
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(token.contract_address);
                alert('Address copied!');
              }}
              title="Click to copy address"
            >
              {token.contract_address.slice(0, 6)}...{token.contract_address.slice(-4)}
              <CopyIcon>📋</CopyIcon>
            </TokenResultAddress>
          </TokenResultItem>
        ))}
        {footerSearch.length >= 2 && filteredTokens.length === 0 && (
          <NoResultsText>No Monad tokens found matching "{footerSearch}"</NoResultsText>
        )}
      </SearchResultsPanel>

      {/* Footer Bar */}
      <FooterContainer>
        <PriceItem>
          <CoinLogo src="https://icons.llamao.fi/icons/chains/rsz_monad.jpg" alt="MON" onError={(e) => {
            // Fallback to a default icon if the URL fails
            (e.target as HTMLImageElement).src = '/landing-assets/ETH_logo.png';
          }} />
          <PriceText>MON {formatPrice(monPrice)}</PriceText>
        </PriceItem>

        <SocialLink
          href="https://x.com/mavrkofficial"
          target="_blank"
          rel="noopener noreferrer"
          title="Follow us on X/Twitter"
        >
          <SocialIcon src="/social_icons/X_icon.png" alt="X/Twitter" />
        </SocialLink>

        <SocialLink
          href="https://t.me/mavrkofficialannouncements"
          target="_blank"
          rel="noopener noreferrer"
          title="Join our Telegram announcements"
        >
          <SocialIcon src="/social_icons/telegram_icon.png" alt="Telegram" />
        </SocialLink>

        {/* GitHub link removed for Monad pages */}

        <SearchContainer>
          <SearchIcon />
          <SearchInput 
            type="text"
            placeholder="Search Monad tokens..."
            value={footerSearch}
            onChange={(e) => setFooterSearch(e.target.value)}
          />
        </SearchContainer>
      </FooterContainer>
    </>
  );
};

export default MonadPriceFooter;

